import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { TodoItem, Project } from "../../../lib/adhd-todo-manager";
import { ADHDAPIAdapter } from "../../../lib/adhd-api-adapter";
import { useAPIClient } from "../../../lib/api-client";
import { OverwhelmAssessment } from "../../../lib/overwhelm-detector";
import { EnergyLevel } from "../../../lib/energy-context-service";

export interface ADHDDataState {
  todos: TodoItem[];
  projects: Project[];
  bodyDoublingSession: {
    taskId: string;
    startTime: Date;
    duration: number;
    checkInInterval: number;
  } | null;
  isLoading: boolean;
  showCreateModal: boolean;
  editingTodo: TodoItem | null;
  overwhelmAssessment: OverwhelmAssessment | null;
  recentUserMessages: string[];
  currentEnergyLevel: EnergyLevel;
  aiPersonalityMode: "supportive" | "focused" | "calm";
}

export interface ADHDDataActions {
  updateTodosOptimistically: (updatedTodo: TodoItem) => void;
  addTodoOptimistically: (newTodo: TodoItem) => void;
  removeTodoOptimistically: (todoId: string) => void;
  addProjectOptimistically: (newProject: Project) => void;
  updateProjectOptimistically: (updatedProject: Project) => void;
  removeProjectOptimistically: (projectId: string) => void;
  startBodyDoubling: (todo: TodoItem) => void;
  setIsLoading: (loading: boolean) => void;
  setShowCreateModal: (show: boolean) => void;
  setEditingTodo: (todo: TodoItem | null) => void;
  setOverwhelmAssessment: (assessment: OverwhelmAssessment | null) => void;
  setRecentUserMessages: (messages: string[]) => void;
  setCurrentEnergyLevel: (level: EnergyLevel) => void;
  setAiPersonalityMode: (mode: "supportive" | "focused" | "calm") => void;
  loadingRef: React.RefObject<boolean>;
  todosRef: React.RefObject<TodoItem[]>;
  projectsRef: React.RefObject<Project[]>;
  recentUserMessagesRef: React.RefObject<string[]>;
}

export const useADHDData = (): ADHDDataState & ADHDDataActions => {
  const { isSignedIn } = useAuth();
  const apiClient = useAPIClient();

  // State
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [bodyDoublingSession, setBodyDoublingSession] = useState<{
    taskId: string;
    startTime: Date;
    duration: number;
    checkInInterval: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoItem | null>(null);
  const [overwhelmAssessment, setOverwhelmAssessment] = useState<OverwhelmAssessment | null>(null);
  const [recentUserMessages, setRecentUserMessages] = useState<string[]>([]);
  const [currentEnergyLevel, setCurrentEnergyLevel] = useState<EnergyLevel>("medium");
  const [aiPersonalityMode, setAiPersonalityMode] = useState<"supportive" | "focused" | "calm">(
    "supportive"
  );

  // Refs
  const loadingRef = useRef(false);
  const todosRef = useRef(todos);
  const projectsRef = useRef(projects);
  const recentUserMessagesRef = useRef(recentUserMessages);

  // Keep refs updated
  useEffect(() => {
    todosRef.current = todos;
    projectsRef.current = projects;
    recentUserMessagesRef.current = recentUserMessages;
  }, [todos, projects, recentUserMessages]);

  // ADHD Adapter
  const adhdAdapter = useMemo(() => {
    const adapter = new ADHDAPIAdapter();
    if (isSignedIn) {
      adapter.setAPIClient(apiClient);
    }
    return adapter;
  }, [isSignedIn, apiClient]);

  // Load initial data when signed in
  useEffect(() => {
    const loadData = async () => {
      if (!isSignedIn || loadingRef.current) return;

      loadingRef.current = true;
      setIsLoading(true);

      try {
        console.log("useADHDData: Loading initial data...");
        const [todosData, projectsData] = await Promise.all([
          adhdAdapter.getTodos(),
          adhdAdapter.getProjects(),
        ]);

        console.log("useADHDData: Data loaded successfully", {
          todosCount: todosData.length,
          projectsCount: projectsData.length,
        });

        setTodos(todosData);
        setProjects(projectsData);
      } catch (error) {
        console.error("useADHDData: Failed to load data:", error);
      } finally {
        setIsLoading(false);
        loadingRef.current = false;
      }
    };

    loadData();
  }, [isSignedIn, adhdAdapter]);

  // Optimistic update actions
  const updateTodosOptimistically = useCallback((updatedTodo: TodoItem) => {
    setTodos(prev => prev.map(todo => (todo.id === updatedTodo.id ? updatedTodo : todo)));
  }, []);

  const addTodoOptimistically = useCallback((newTodo: TodoItem) => {
    setTodos(prev => [newTodo, ...prev]);
  }, []);

  const removeTodoOptimistically = useCallback((todoId: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== todoId));
  }, []);

  const addProjectOptimistically = useCallback((newProject: Project) => {
    setProjects(prev => [newProject, ...prev]);
  }, []);

  const updateProjectOptimistically = useCallback((updatedProject: Project) => {
    setProjects(prev =>
      prev.map(project => (project.id === updatedProject.id ? updatedProject : project))
    );
  }, []);

  const removeProjectOptimistically = useCallback((projectId: string) => {
    setProjects(prev => prev.filter(project => project.id !== projectId));
  }, []);

  // Start body doubling session
  const startBodyDoubling = useCallback(
    (todo: TodoItem) => {
      const duration = todo.estimated_minutes || 25;
      const checkInInterval = Math.max(5, Math.floor(duration / 4));

      setBodyDoublingSession({
        taskId: todo.id,
        startTime: new Date(),
        duration,
        checkInInterval,
      });

      // Update task status to in_progress if pending
      if (todo.status === "pending") {
        updateTodosOptimistically({ ...todo, status: "in_progress" });
        adhdAdapter.updateTodo({ todo_id: todo.id, status: "in_progress" }).catch(err => {
          console.error("Failed to update task status:", err);
        });
      }
    },
    [updateTodosOptimistically, adhdAdapter]
  );

  return {
    // State
    todos,
    projects,
    bodyDoublingSession,
    isLoading,
    showCreateModal,
    editingTodo,
    overwhelmAssessment,
    recentUserMessages,
    currentEnergyLevel,
    aiPersonalityMode,

    // Actions
    updateTodosOptimistically,
    addTodoOptimistically,
    removeTodoOptimistically,
    addProjectOptimistically,
    updateProjectOptimistically,
    removeProjectOptimistically,
    startBodyDoubling,
    setIsLoading,
    setShowCreateModal,
    setEditingTodo,
    setOverwhelmAssessment,
    setRecentUserMessages,
    setCurrentEnergyLevel,
    setAiPersonalityMode,
    loadingRef,
    todosRef,
    projectsRef,
    recentUserMessagesRef,
  };
};
